import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import { APP_CONFIG } from '../app-config.token';
import { AppConfig } from '../app-config';
import { CachedHttpClient } from '../common/utils';
import { LegalizationDecisionDto } from '../models/dtos/legalization-decision-dto';
import { AllLegalizationGridDto } from '../models/dtos/all-legalization-grid-dto';
import { IPagedResult } from '../shared/models/PagedResult';
import { Filter } from '../models/common/filter';
import { WorkerLegalizationAgreementDto } from '../models/dtos/worker-legalization-agreement-dto';
import { WorkerLegalizationActiveFileDto } from '../models/dtos/worker-legalization-active-file-dto';
import { LegalizationInformationDto } from '../models/dtos/LegalizationInformationDto';
import { CorrectLegalizationDocumentBasicDto, LegalizationDocumentBasicDto, LegalizationDocumentDto } from '../models/dtos/legalization-document-files-dto';
import { LegalizationDocumentInfoDto } from '../models/dtos/legalization-document-info-dto';
import { LegalizationDocumentInformationDto } from '../models/dtos/legalization-document-information-dto';
import { LegalizationDetailsDto } from '../models/dtos/legalization-details-dto';
import { AssignDocumentToLegalizationProcessesDto } from '../models/dtos/assign-document-to-legalization-processes-dto';
import { AssignedLegalizationProcessDto } from '../models/dtos/assigned-legalization-process-dto';
import { EditLegalizationDocumentRequest } from '../contracts/requests/edit-legalization-document-request';
import { LegalizationDocumentSummaryDto } from '../models/dtos/legalization-document-summary-dto';
import { SubmitForLegalizationDto } from '../models/dtos/submit-for-legalization-dto';
import { SubmitForMultipleLegalizationsDto } from "../models/dtos/submit-for-legalization-multiple-dto";

@Injectable({
  providedIn: 'root',
})
export class LegalizationService {
  private http = inject(HttpClient);
  private cachedHttp = inject(CachedHttpClient);
  private config = inject<AppConfig>(APP_CONFIG);

  private readonly API_URL = this.config.resourceApiURI;
  private readonly legalizationPath = '/legalizations';

  private get legalizationUrl() {
    return this.API_URL + this.legalizationPath;
  }

  constructor() { }

  hasAccessToLegalization(): Observable<boolean> {
    const url = `${this.legalizationPath}/hasAccessToLegalization`;
    return this.cachedHttp.get<boolean>(url);
  }

  getAllLegalizationGrid(
    page: number,
    count: number,
    sortingField: string,
    sortingDirection: string = 'desc',
    filters: Filter[],
  ): Observable<IPagedResult<AllLegalizationGridDto>> {
    let params = new HttpParams()
      .set('page', `${page || 1}`)
      .set('count', `${count || 10}`)
      .set('sortingField', sortingField)
      .set('sortingDirection', sortingDirection)
      .set('filterString', JSON.stringify(filters));

    const url = `${this.legalizationUrl}/allLegalizations`;
    return this.http.get<IPagedResult<AllLegalizationGridDto>>(url, { params: params }).pipe(
      map((pagedResult) => ({
        ...pagedResult,
        Results: pagedResult.Results.map((a) => ({
          ...a,
          DocumentStartDate: a.DocumentStartDate && new Date(a.DocumentStartDate + 'Z'),
          DocumentEndDate: a.DocumentEndDate && new Date(a.DocumentEndDate + 'Z'),
          DocumentCreatedOn: a.DocumentCreatedOn && new Date(a.DocumentCreatedOn + 'Z'),
          StartDate: a.StartDate && new Date(a.StartDate + 'Z'),
          EndDate: a.EndDate && new Date(a.EndDate + 'Z'),
        }))
      })),
    );
  }

  getWorkerLegalizationAgreements(workerId: number, includeInactive: boolean): Observable<WorkerLegalizationAgreementDto[]> {
    const params = new HttpParams().set('includeInactive', includeInactive.toString());
    const url = `${this.legalizationUrl}/agreements/workers/${workerId}`;
    return this.http.get<WorkerLegalizationAgreementDto[]>(url, { params }).pipe(
      map((agreements) => agreements.map((agreement) => ({
        ...agreement,
        EmploymentDateFrom: agreement.EmploymentDateFrom && new Date(agreement.EmploymentDateFrom + 'Z'),
        EmploymentDateTo: agreement.EmploymentDateTo && new Date(agreement.EmploymentDateTo + 'Z'),
      })))
    );
  }

  getWorkerLegalizationActiveFiles(workerId: number): Observable<WorkerLegalizationActiveFileDto[]> {
    const url = `${this.legalizationUrl}/files/workers/${workerId}`;
    return this.http.get<WorkerLegalizationActiveFileDto[]>(url);
  }

  saveLegalizationInformation(saveLegalizationInformationDto: LegalizationInformationDto): Observable<any> {
    const url = `${this.legalizationUrl}/saveLegalizationInformation`;
    return this.http.put<any>(url, saveLegalizationInformationDto);
  }

  saveLegalizationDocument(saveLegalizationDocumentDto: LegalizationDocumentDto): Observable<any> {
    const url = `${this.legalizationUrl}/saveLegalizationDocument`;
    return this.http.put<any>(url, saveLegalizationDocumentDto);
  }

  saveLegalizationDocumentByEmployee(saveLegalizationDocumentDto: LegalizationDocumentBasicDto): Observable<any> {
    const url = `${this.legalizationUrl}/saveLegalizationEmployeeDocument`;
    return this.http.put<any>(url, saveLegalizationDocumentDto);
  }

  correctLegalizationDocumentByEmployee(saveLegalizationDocumentDto: CorrectLegalizationDocumentBasicDto): Observable<any> {
    const url = `${this.legalizationUrl}/correctLegalizationDocument`;
    return this.http.put<any>(url, saveLegalizationDocumentDto);
  }

  getLegalizationDocumentByEmployee(legalizationDocumentId: number): Observable<LegalizationDocumentBasicDto> {
    const url = `${this.legalizationUrl}/getLegalizationDocumentBasicInfo/${legalizationDocumentId}`;
    return this.http.get<any>(url);
  }

  getLegalizationDocumentByCoordinator(legalizationDocumentId: number): Observable<LegalizationDocumentInfoDto> {
    const url = `${this.legalizationUrl}/getLegalizationDocumentInfo/${legalizationDocumentId}`;
    return this.http.get<LegalizationDocumentInfoDto>(url);
  }

  getLegalizationDocumentByLegalizer(legalizationDocumentId: number): Observable<LegalizationDocumentInformationDto> {
    const url = `${this.legalizationUrl}/getLegalizationInformation/${legalizationDocumentId}`;
    return this.http.get<LegalizationDocumentInformationDto>(url);
  }

  getLegalizationDetails(legalizationId: number, legalizationDocumentId?: number): Observable<LegalizationDetailsDto> {
    let url = `${this.legalizationUrl}/getLegalizationDetails/${legalizationId}`;
    let params = new HttpParams();
    if (legalizationDocumentId) {
      params = params.set('legalizationDocumentId', legalizationDocumentId.toString());
    }
    return this.http.get<LegalizationDetailsDto>(url, { params }).pipe(
      map(
        (a) =>
          <LegalizationDetailsDto>{
            ...a,
            DateOfBirth: a.DateOfBirth && new Date(a.DateOfBirth + 'Z'),
            AgreementDetails: {
              ...a.AgreementDetails,
              EmploymentDateFrom: a.AgreementDetails?.EmploymentDateFrom && new Date(a.AgreementDetails.EmploymentDateFrom + 'Z'),
              EmploymentDateTo: a.AgreementDetails?.EmploymentDateTo && new Date(a.AgreementDetails.EmploymentDateTo + 'Z'),
              Wages: a.AgreementDetails?.Wages
            },
            DocumentDetails: {
              ...a.DocumentDetails,
              StartDate: a.DocumentDetails?.StartDate && new Date(a.DocumentDetails.StartDate + 'Z'),
              EndDate: a.DocumentDetails?.EndDate && new Date(a.DocumentDetails.EndDate + 'Z'),
            },
            OtherDocuments: a.OtherDocuments.map((doc) => ({
              ...doc,
              ValidTo: doc.ValidTo && new Date(doc.ValidTo + 'Z'),
            })),
            OtherAgreements: a.OtherAgreements.map((doc) => ({
              ...doc,
              StartDate: doc.StartDate && new Date(doc.StartDate + 'Z'),
              EndDate: doc.EndDate && new Date(doc.EndDate + 'Z')
            })),
          }
      ),
    );;
  }

  editLegalizationDocument(documentId: number, request: EditLegalizationDocumentRequest): Observable<any> {
    const url = `${this.legalizationUrl}/documents/${documentId}`;
    return this.http.put<any>(url, request);
  }

  getAllLegalizationDocuments(page: number,
    count: number,
    sortingField: string,
    sortingDirection: string = 'desc',
    filters: Filter[],
    includeInactive: boolean = true)
    : Observable<IPagedResult<LegalizationDocumentSummaryDto>> {

    let params = new HttpParams()
      .set('page', `${page || 1}`)
      .set('count', `${count || 10}`)
      .set('sortingField', sortingField)
      .set('sortingDirection', sortingDirection)
      .set('filterString', JSON.stringify(filters))
      .set('includeInactive', includeInactive);

    const url = `${this.legalizationUrl}/documents/`;
    return this.http.get<IPagedResult<LegalizationDocumentSummaryDto>>(url, { params: params }).pipe(
      map((pagedResult) => ({
        ...pagedResult,
        Results: pagedResult.Results.map((doc) => ({
          ...doc,
          DocumentStartDate: doc.DocumentStartDate && new Date(doc.DocumentStartDate + 'Z'),
          DocumentEndDate: doc.DocumentEndDate && new Date(doc.DocumentEndDate + 'Z'),
        }))
      })),
    );
  }

  assignDocumentsToLegalizationProcesses(assignDocumentToLegalizationProcessesDto: AssignDocumentToLegalizationProcessesDto): Observable<any> {
    const url = `${this.legalizationUrl}/assignDocumentsToLegalizationProcesses`;
    return this.http.post<any>(url, assignDocumentToLegalizationProcessesDto);
  }

  getAssignedLegalizationProcesses(legalizationDocumentId: number): Observable<AssignedLegalizationProcessDto> {
    const url = `${this.legalizationUrl}/getAssignedLegalizationProcesses/${legalizationDocumentId}`;
    return this.http.get<AssignedLegalizationProcessDto>(url);
  }

  submitForLegalization(submitForLegalization: SubmitForLegalizationDto): Observable<any> {
    const url = `${this.legalizationUrl}/submitForLegalization`;
    return this.http.post<any>(url, submitForLegalization);
  }

  submitForMultipleLegalizations(submitForLegalization: SubmitForMultipleLegalizationsDto): Observable<number[]> {
    const url = `${this.legalizationUrl}/SubmitForMultipleLegalizations`;
    return this.http.post<number[]>(url, submitForLegalization);
  }
}
